import * as React from 'react';
import PropTypes from 'prop-types';
import MuiAlert from '@mui/material/Alert';
import { Snackbar, Stack } from '@mui/material';

/**
 * Alert component using Material-UI's MuiAlert.
 *
 * @param {object} props - Component props.
 * @param {React.Ref} ref - Reference to the component.
 * @returns {JSX.Element} A MuiAlert component.
 */
const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

/**
 * CustomizedSnackbars component shows a Snackbar with a customizable alert message.
 *
 * @component
 * @param {object} props - Component props.
 * @param {'success'|'error'} [props.messageType='success'] - The type of the message (success or error).
 * @param {string} [props.message='Successfully saved!'] - The message to display.
 * @returns {JSX.Element} A stack containing a Snackbar component.
 */
function CustomizedSnackbars(props) {
    const {
        messageType = 'success',
        message = 'Successfully saved!'
    } = props;
    const [open, setOpen] = React.useState(true);

    /**
     * Handles the closing of the Snackbar.
     *
     * @param {object} event - The event source of the callback.
     * @param {string} reason - The reason for the Snackbar closing.
     */
    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            <Snackbar 
                open={open} 
                autoHideDuration={6000} 
                onClose={handleClose}>
                <Alert 
                    onClose={handleClose} 
                    severity={messageType}>
                    {message}
                </Alert>
            </Snackbar>
        </Stack>
    );
}

CustomizedSnackbars.propTypes = {
    messageType: PropTypes.oneOf([
        'success',
        'error'
    ]),
    message: PropTypes.string
};

export default CustomizedSnackbars;
